import { BRANDING } from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';
import Add from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import {
  IPublicationModalMessages,
  ISessionUser,
  ISettings,
  IStoriesFolder,
  IStoryListMessages
} from '../../../../types';
import { CreatePublicationModal } from '../../Publications/CreatePublicationModal/CreatePublicationModal';
import { CreateStoryModal } from '../CreateStoryModal/CreateStoryModal';

import { styles } from './PageHeader.styles';

export type IPageHeaderProps = {
  displayPublicationTab: boolean;
  publicationsCount: number;
  isPublicationTabSelected: boolean;
  canOrganizeStories: boolean;
  currentFolderId: string;
  entityId: number;
  entityName: string;
  entitySubdomain: string;
  folderStructure: IStoriesFolder[];
  isAdmin: boolean;
  isCreateStoryModalVisible: boolean;
  isTemplate: boolean;
  messages: IStoryListMessages;
  publicationModalMessages: IPublicationModalMessages;
  settings: ISettings;
  user: ISessionUser;
  search?: string;
  publicationList?: IPublication[];
  navigateToSettings(tab: string): any;
  navigateToCreateFromTemplate(): any;
  setIsCreateStoryModalVisible(isVisible: boolean): void;
};

export const PageHeader: React.FC<IPageHeaderProps> = ({
  publicationsCount,
  publicationList,
  displayPublicationTab,
  isPublicationTabSelected,
  canOrganizeStories,
  currentFolderId,
  entityId,
  entityName,
  entitySubdomain,
  folderStructure,
  isAdmin,
  publicationModalMessages,
  isCreateStoryModalVisible,
  messages,
  settings,
  user,
  search,
  navigateToSettings,
  navigateToCreateFromTemplate,
  setIsCreateStoryModalVisible
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isCreatePublicationModalVisible, setIsCreatePublicationModalVisible] = useState(false);

  const handleNewStory = () => {
    setMenuAnchorEl(null);
    setIsCreateStoryModalVisible(true);
  };

  const handleTemplateList = () => {
    setMenuAnchorEl(null);
    navigateToCreateFromTemplate();
  };

  return (
    <Box sx={styles.header}>
      <div id="skipToContent">
        <a href="#mainContent">{messages.skipTo}</a>
      </div>

      <Box sx={styles.headerContent}>
        <Typography variant="h2" component="h2" data-test="reportTitle" sx={styles.title}>
          {displayPublicationTab ? messages.storiesAndPublications : messages.stories}
        </Typography>

        {isAdmin && (
          <Stack direction="row" sx={styles.buttons}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsOutlined aria-hidden={true} />}
              onClick={() => navigateToSettings(BRANDING)}
              data-test="settings"
            >
              {messages.settings}
            </Button>

            {!isPublicationTabSelected && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<ArrowDropDown aria-hidden={true} />}
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  data-test="create-new-story-button"
                >
                  {messages.createNewStory}
                </Button>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={() => setMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={handleNewStory} data-test="dashboard-new-story-blank">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{messages.newStory}</Typography>
                      <Typography variant="caption" color="text.secondary">{messages.newStart}</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={handleTemplateList} data-test="dashboard-new-story-template">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{messages.template}</Typography>
                      <Typography variant="caption" color="text.secondary">{messages.templateStart}</Typography>
                    </Box>
                  </MenuItem>
                </Menu>
              </>
            )}

            {isPublicationTabSelected && (publicationsCount > 0 || (search?.length ?? 0) > 0) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add aria-hidden={true} />}
                onClick={() => setIsCreatePublicationModalVisible(true)}
                data-test="create-publication-button"
              >
                {publicationModalMessages.createNewPublication}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {isCreateStoryModalVisible &&
        !isPublicationTabSelected &&
        createPortal(
          <CreateStoryModal
            isAdmin={isAdmin}
            canOrganizeStories={canOrganizeStories}
            currentFolderId={currentFolderId}
            folderStructure={folderStructure}
            messages={messages}
            entityName={entityName}
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            settings={settings}
            user={user}
            onClose={() => setIsCreateStoryModalVisible(false)}
          />,
          document.getElementById('app') ?? document.body
        )}

      {isCreatePublicationModalVisible &&
        createPortal(
          <CreatePublicationModal
            isAdmin={isAdmin}
            messages={publicationModalMessages}
            folderStructure={folderStructure}
            onClose={() => setIsCreatePublicationModalVisible(false)}
            user={user}
            entitySubdomain={entitySubdomain}
            entityId={entityId}
            entityName={entityName}
            publicationList={publicationList}
          />,
          document.getElementById('app') ?? document.body
        )}
    </Box>
  );
};
